import { useTaskEvents } from "@/hooks/use-task-events";
import { formatMessage, reconciliationResultMessages } from "@/services/messages";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Прогресс-бар активного прогона (FR-003).
 *
 * Подключается к task-queue через `useTaskEvents(taskId)` и отображает:
 * - визуальный progress-bar с %;
 * - текст фазы из event'а `progress.message` (с дефолтом из messages);
 * - длительность с момента старта компонента.
 *
 * Колбэки `onComplete(runId)` и `onError(message)` вызываются при
 * соответствующих событиях task-queue (фича 014, ADR-001).
 */
export interface RunProgressBarProps {
	taskId: string;
	/** Колбэк при `complete` event'е. Получает `runId` из payload'а (если он там есть). */
	onComplete?: (runId: string | null) => void;
	/** Колбэк при `error` event'е. Получает `message` из payload'а. */
	onError?: (message: string) => void;
}

interface ProgressEventData {
	message?: string;
	percent?: number;
}

interface CompleteEventData {
	runId?: string;
}

interface ErrorEventData {
	message?: string;
}

export function RunProgressBar({ taskId, onComplete, onError }: RunProgressBarProps) {
	const messages = reconciliationResultMessages.run;
	const [phase, setPhase] = useState<string>(messages.progress_phase_default);
	const [percent, setPercent] = useState<number | null>(null);
	const [startedAt] = useState<number>(() => Date.now());
	const [elapsedMs, setElapsedMs] = useState<number>(0);

	useTaskEvents(taskId, (event, data) => {
		if (event === "progress") {
			const payload = data as ProgressEventData;
			if (payload.message) setPhase(payload.message);
			if (typeof payload.percent === "number") setPercent(payload.percent);
		} else if (event === "complete") {
			const payload = data as CompleteEventData;
			onComplete?.(payload.runId ?? null);
		} else if (event === "error") {
			const payload = data as ErrorEventData;
			onError?.(payload.message ?? "");
		}
	});

	useEffect(() => {
		const interval = setInterval(() => {
			setElapsedMs(Date.now() - startedAt);
		}, 1000);
		return () => clearInterval(interval);
	}, [startedAt]);

	const elapsedSeconds = Math.floor(elapsedMs / 1000);
	const widthStyle: React.CSSProperties = {
		width: percent === null ? undefined : `${Math.max(0, Math.min(100, percent))}%`,
	};

	return (
		<div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
			<div className="flex items-center gap-3">
				<Loader2 className="h-4 w-4 animate-spin text-blue-600" />
				<div className="flex-1">
					<div className="flex items-baseline justify-between gap-3">
						<span className="text-sm font-medium text-blue-900">{phase}</span>
						<span className="text-xs text-blue-700">
							{formatMessage(messages.progress_elapsed_template, { seconds: elapsedSeconds })}
						</span>
					</div>
					<div className="mt-2 h-1.5 overflow-hidden rounded-full bg-blue-100">
						{percent === null ? (
							<div className="h-full w-1/3 animate-pulse rounded-full bg-blue-400" />
						) : (
							<div
								className="h-full rounded-full bg-blue-500 transition-[width] duration-300"
								style={widthStyle}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
